import React, { useEffect, useState } from "react";
import { useStore } from "react-redux";
import type { Action, AnyAction } from "redux";

import { buildMessages } from "@eroad/i18n/buildMessages";
import { useIntl } from "@eroad/i18n/useIntl";

import { isDefined } from "../utils/isDefined";
import type { EroadStore, InjectDomainProps, InjectDomainReturn } from "./types";

/**
 * It allows domains to "register" their reducers/sagas/messages
 * to the main app, rather than the main app having to import them all.
 *
 * The injectDomain HOC that it creates should be used on all route entry components
 * for the associated domain.
 */
const createInjectDomain =
  <S = any, A extends Action = AnyAction>({ key, reducer, reducers, saga, translations }: InjectDomainProps<S, A>) =>
  (Component: React.ComponentType<any> | React.MemoExoticComponent<React.ComponentType<any>>): InjectDomainReturn => {
    const messages = translations && buildMessages(translations);

    /**
     * This is needed because of how we use injectDomain in tests as we wanna have the same api that render, mount, shallow, etc. have
     * i.e. we're passing jsx to this function instead of a reference to the component: injectDomain(<Component />) vs injectDomain(Component)
     */
    /* istanbul ignore next -- legacy pattern for test convenience when JSX is passed instead of component reference */
    const isJsx = typeof Component === "object" && React.isValidElement(Component);

    return function InjectedComponent(props: Record<string, unknown>): JSX.Element | null {
      const store = useStore() as EroadStore<S, A>;
      const intl = useIntl();

      const [injected, setInjected] = useState(isDefined(store.asyncReducers[key]));
      const [localeInjected, setLocaleInjected] = useState(false);

      useEffect(() => {
        if (messages) {
          /**
           * Using mutation here solves a few problems.
           * It provides messages injection without risking re-rendering everything
           * consuming the current intl context due to changing the messages object.
           * Nested IntlProviders don't provide fallback lookup to parent IntlProviders
           * so that option is not ideal either.
           */
          Object.assign(intl.messages, messages[intl.locale]);
        }

        /**
         * We must always ensure that this useEffect has run at least once before rendering the component. messages may have changed due to a locale change between visits.
         * This is due to configureStore injecting only reducers and sagas, not translations.
         * Even if the domain's reducers and sagas are already injected, we need to ensure that the messages are injected too.
         */
        setLocaleInjected(true);

        /**
         * Reducers and sagas are injected only once, so we need to check if they are already injected.
         */
        if (!injected) {
          if (reducer) {
            store.injectReducer(key, reducer);
          }

          if (reducers) {
            Object.entries(reducers).forEach(([ithKey, ithReducer]) => {
              store.injectReducer(ithKey, ithReducer);
            });
          }

          if (saga) {
            store.injectSaga(key, saga);
          }

          setInjected(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [intl.locale]);

      /* istanbul ignore next -- legacy pattern for test convenience when JSX is passed instead of component reference */
      if (isJsx) {
        return injected && localeInjected ? <>{React.cloneElement(Component, props)}</> : null;
      }

      /**
       * It's not safely possible to avoid this extra render
       *
       * @see https://github.com/react-boilerplate/redux-injectors/issues/19
       */
      return injected && localeInjected ? <Component {...props} /> : null;
    };
  };

export { createInjectDomain };
